import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Copy, Check } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import type { Contact } from '@/types/entities'
import { sortedCountryCodes } from '@/lib/data/country-codes'

interface ContactViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    contact: Contact | null
}

export function ContactViewDialog({ open, onOpenChange, contact }: ContactViewDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [copiedEmail, setCopiedEmail] = useState(false)
    const [copiedPhone, setCopiedPhone] = useState(false)
    
    if (!contact) return null

    const countryInfo = contact.phonePrefix
        ? sortedCountryCodes.find(c => c.dialCode === contact.phonePrefix)
        : null

    const handleCopyEmail = async () => {
        if (!contact.email) return
        try {
            await navigator.clipboard.writeText(contact.email)
            setCopiedEmail(true)
            setTimeout(() => setCopiedEmail(false), 2000)
            toast({
                title: t('contacts.copied'),
                description: t('contacts.emailCopied'),
            })
        } catch (error) {
            toast({
                title: t('contacts.copyFailed'),
                description: t('contacts.failedToCopy'),
                variant: 'destructive',
            })
        }
    }

    const handleCopyPhone = async () => {
        const phoneNumber = `${contact.phonePrefix || ''}${contact.phone || ''}`.trim()
        if (!phoneNumber) return
        try {
            await navigator.clipboard.writeText(phoneNumber)
            setCopiedPhone(true)
            setTimeout(() => setCopiedPhone(false), 2000)
            toast({
                title: t('contacts.copied'),
                description: t('contacts.phoneCopied'),
            })
        } catch (error) {
            toast({
                title: t('contacts.copyFailed'),
                description: t('contacts.failedToCopy'),
                variant: 'destructive',
            })
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('contacts.viewContact')}</DialogTitle>
                    <DialogDescription>
                        {t('contacts.viewContactDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Contact Info */}
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">
                                {t('contacts.firstName')}
                            </label>
                            <p className="text-lg font-semibold">{contact.firstName || <span className="text-muted-foreground">—</span>}</p>
                        </div>

                        {contact.surname && (
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    {t('contacts.surname')}
                                </label>
                                <p className="text-base">{contact.surname}</p>
                            </div>
                        )}

                        {contact.email && (
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    {t('contacts.email')}
                                </label>
                                <div className="flex items-center gap-2">
                                    <p className="text-base">{contact.email}</p>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={handleCopyEmail}
                                        title={t('contacts.copyEmail')}
                                    >
                                        {copiedEmail ? (
                                            <Check className="h-4 w-4 text-green-600" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {(contact.phonePrefix || contact.phone) && (
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    {t('contacts.phone')}
                                </label>
                                <div className="flex items-center gap-2">
                                    <p className="text-base">
                                        {contact.phonePrefix && <span>{contact.phonePrefix} </span>}
                                        {contact.phone || <span className="text-muted-foreground">—</span>}
                                    </p>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={handleCopyPhone}
                                        title={t('contacts.copyPhone')}
                                    >
                                        {copiedPhone ? (
                                            <Check className="h-4 w-4 text-green-600" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {contact.position && (
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    {t('contacts.position')}
                                </label>
                                <p className="text-base">{contact.position}</p>
                            </div>
                        )}

                        {contact.team && (
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    {t('contacts.team')}
                                </label>
                                <p className="text-base">{contact.team}</p>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
